/**
 * A family's backup, and putting it back.
 *
 * WHY THIS EXISTS. Everything in Valley is encrypted on the family's devices
 * under the family key, and the server never holds that key. The family code
 * unlocks it on a new device. So a family that loses its code AND every
 * signed-in device has lost the key -- and nobody, including this project,
 * can open what is left on the server. Two things stand against that:
 *
 *   the recovery sheet   the family code and each child's code, on paper
 *                        (RecoverySheet.tsx) -- the key back, in a drawer
 *   this file            everything readable, on the parent's own disk, for
 *                        the day even the paper is gone
 *
 * THE FILE IS PLAIN. It holds names, amounts and reasons in the clear, like
 * the CSV export, because a backup that needs the lost key to open is not a
 * backup. The app says so where it offers the download.
 *
 * RESTORING puts it into a NEW, EMPTY family: the one a parent creates after
 * losing everything. It uses the ordinary routes -- people, entries, goals,
 * shortcuts, allowance -- with the ORIGINAL ids, so a restore that stops half
 * way is simply run again and writes each thing once. The children get new
 * codes (a new family is a new set of them), so the last step is a new
 * recovery sheet; each child types their new code once and a parent approves
 * the device, and their page shows everything it did before.
 *
 * Every balance is rebuilt EXACTLY. An entry split unevenly between people --
 * a completed goal takes what each child put in, not an even share -- would
 * be split evenly if replayed as one entry, so it is replayed as one entry per
 * person with their exact share, and the family's part on its own.
 */

import { loadJournal } from './journal';
import { mintChildCode } from './devices';
import { readFamilyCode, sealNameUnderCode } from './families';
import { asTyped } from './money';
import {
  loadState,
  missGoal,
  recordMoney,
  redeemGoal,
  saveAllowance,
  saveGoal,
  saveQuickAction,
  savePerson,
  saveSettings,
  setGoalFunds,
  type AllowancePlan,
  type Entry,
  type Every,
  type FamilyState,
  type GoalIcon,
  type GoalKind,
} from './state';
import type { AvatarSpec } from '../avatar/avatar';

export const BACKUP_KIND = 'valley-backup';
export const BACKUP_VERSION = 1;

export interface Backup {
  kind: typeof BACKUP_KIND;
  version: number;
  exportedAt: string;
  familyName: string;
  people: Array<{ id: string; name: string; avatar: AvatarSpec | null }>;
  /** Only what counts: entries taken back are left out. */
  entries: Array<{
    id: string;
    date: string;
    amount: number;
    kind: string;
    reason: string;
    people: string[];
    shares: Record<string, number>;
  }>;
  goals: Array<{
    id: string;
    title: string;
    target: number;
    kind: GoalKind;
    icon: GoalIcon;
    due: string | null;
    status: 'active' | 'completed' | 'missed';
    people: string[];
    funds: Record<string, number>;
  }>;
  quickActions: Array<{ id: string; reason: string; amount: number; people: string[] }>;
  allowance: Record<string, { amount: number; every: Every; start: string; paused: boolean }>;
}

/** Every entry, month by month: one read is capped, a month never is. */
async function everyEntry(familyId: string, key: CryptoKey): Promise<Entry[]> {
  const first = await loadJournal(familyId, key);
  if (!first.truncated) return first.entries;
  const all: Entry[] = [];
  for (const month of first.months) {
    all.push(...(await loadJournal(familyId, key, { month })).entries);
  }
  return all;
}

export async function makeBackup(familyId: string, key: CryptoKey, state: FamilyState): Promise<Backup> {
  const entries = await everyEntry(familyId, key);
  return {
    kind: BACKUP_KIND,
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    familyName: state.familyName,
    people: state.people
      .filter((p) => p.status === 'approved')
      .map((p) => ({ id: p.id, name: p.name, avatar: p.avatar })),
    entries: entries
      .filter((e) => !e.voided)
      .map((e) => ({ id: e.id, date: e.date, amount: e.amount, kind: e.kind, reason: e.reason, people: e.people, shares: e.shares }))
      .sort((a, b) => a.date.localeCompare(b.date)),
    goals: state.goals
      .filter((g) => g.status === 'active' || g.status === 'completed' || g.status === 'missed')
      .map((g) => ({
        id: g.id,
        title: g.title,
        target: g.target,
        kind: g.kind,
        icon: g.icon,
        due: g.due,
        status: g.status as 'active' | 'completed' | 'missed',
        people: g.people,
        funds: g.status === 'active' ? g.funds : {},
      })),
    quickActions: state.quickActions.map((q) => ({ id: q.id, reason: q.reason, amount: q.amount, people: q.people })),
    allowance: Object.fromEntries(
      Object.entries(state.allowance).map(([who, p]: [string, AllowancePlan]) => [
        who,
        { amount: p.amount, every: p.every, start: p.start, paused: p.paused },
      ]),
    ),
  };
}

/** Hand the file to the parent. Built here; nothing is sent anywhere. */
export function downloadBackup(backup: Backup): void {
  const slug = (backup.familyName || 'valley').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const day = backup.exportedAt.slice(0, 10);
  const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${slug || 'valley'}-backup-${day}.json`;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** A file somebody chose, checked before anything is written from it. */
export function parseBackup(text: string): Backup {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('That file is not a Birbly backup.');
  }
  const b = data as Partial<Backup>;
  if (!b || b.kind !== BACKUP_KIND) throw new Error('That file is not a Birbly backup.');
  if (typeof b.version !== 'number' || b.version > BACKUP_VERSION) {
    throw new Error('That backup was made by a newer version of Birbly. Update the app and try again.');
  }
  for (const field of ['people', 'entries', 'goals', 'quickActions'] as const) {
    if (!Array.isArray(b[field])) throw new Error('That backup is incomplete.');
  }
  if (!b.allowance || typeof b.allowance !== 'object') throw new Error('That backup is incomplete.');
  return b as Backup;
}

/** What a restore will put back, for the question before it starts. */
export function summarise(backup: Backup): { people: number; entries: number; goals: number; balance: number } {
  return {
    people: backup.people.length,
    entries: backup.entries.length,
    goals: backup.goals.length,
    balance: backup.entries.reduce((sum, e) => sum + e.amount, 0),
  };
}

/** `domain.shares`, exactly: evenly to the cent, the odd cents on the first. */
function evenShares(amount: number, count: number): number[] {
  const sign = amount < 0 ? -1 : 1;
  const base = Math.floor(Math.abs(amount) / count);
  const extra = Math.abs(amount) % count;
  return Array.from({ length: count }, (_, i) => sign * (base + (i < extra ? 1 : 0)));
}

/**
 * How to replay one entry so every balance comes back to the cent: as it
 * was, when the server's even split would reproduce it; otherwise one entry
 * per person with their exact share, and the family's part on its own.
 */
export function replay(entry: Backup['entries'][number]): Array<{ id: string; amount: number; people: string[] }> {
  const n = entry.people.length;
  const even = n ? evenShares(entry.amount, n) : [];
  const same = entry.people.every((pid, i) => (entry.shares[pid] ?? 0) === even[i]);
  if (same) return [{ id: entry.id, amount: entry.amount, people: entry.people }];
  const parts = entry.people
    .map((pid, i) => ({ id: `${entry.id}-p${i}`, amount: entry.shares[pid] ?? 0, people: [pid] }))
    .filter((p) => p.amount !== 0);
  const rest = entry.amount - parts.reduce((sum, p) => sum + p.amount, 0);
  if (rest !== 0) parts.push({ id: `${entry.id}-f`, amount: rest, people: [] });
  return parts;
}

/**
 * Put a backup into this family, which must be new and empty.
 *
 * `onStep` is told what is happening, for a progress line. Safe to run again
 * after a failure: every write uses the backup's own ids.
 */
export async function restoreBackup(
  familyId: string,
  key: CryptoKey,
  backup: Backup,
  onStep: (words: string) => void = () => {},
): Promise<{ codes: number }> {
  const before = await loadState(familyId, key);
  const known = new Set(backup.people.map((p) => p.id));
  const strangers = before.people.filter((p) => p.status !== 'removed' && !known.has(p.id));
  const foreignEntries = before.entries.filter((e) => !backup.entries.some((b) => e.id === b.id || e.id.startsWith(`${b.id}-`)));
  if (strangers.length || foreignEntries.length) {
    throw new Error('Restoring is only for a new, empty family. This one already has its own people or money in it.');
  }

  onStep('Your family’s name');
  const code = await readFamilyCode(familyId, key).catch(() => null);
  await saveSettings(familyId, key, {
    name: backup.familyName || 'Our family',
    ...(code ? { preview: await sealNameUnderCode(backup.familyName, code) } : {}),
  });

  onStep('People');
  for (const person of backup.people) {
    const existing = before.people.find((p) => p.id === person.id);
    await savePerson(
      familyId,
      key,
      { name: person.name, ...(person.avatar ? { avatar: person.avatar } : {}) },
      { id: person.id, key: existing?.key ?? null },
    );
  }
  // With their new keys, so every reason is sealed for the child it names.
  const roster = (await loadState(familyId, key)).people;
  const here = new Set(roster.map((p) => p.id));

  let done = 0;
  for (const entry of backup.entries) {
    done += 1;
    if (done % 10 === 1) onStep(`Money: ${done} of ${backup.entries.length}`);
    for (const part of replay(entry)) {
      await recordMoney(
        familyId,
        key,
        {
          id: part.id,
          amount: asTyped(part.amount),
          reason: entry.reason || 'Restored',
          people: part.people.filter((pid) => here.has(pid)),
          date: entry.date,
        },
        roster,
      );
    }
  }

  onStep('Goals');
  for (const goal of backup.goals) {
    try {
      await saveGoal(
        familyId,
        key,
        {
          id: goal.id,
          title: goal.title,
          target: asTyped(goal.target),
          kind: goal.kind,
          icon: goal.icon,
          ...(goal.due ? { due: goal.due } : {}),
          people: goal.people.filter((pid) => here.has(pid)),
        },
        roster,
      );
    } catch {
      // Already restored and finished on an earlier run: it cannot be edited.
      continue;
    }
    if (goal.status === 'missed') await missGoal(familyId, goal.id);
    // Its spend is already in the journal above, so it completes empty.
    if (goal.status === 'completed') await redeemGoal(familyId, key, goal.id);
    if (goal.status === 'active' && Object.keys(goal.funds).length) {
      await setGoalFunds(
        familyId,
        goal.id,
        Object.fromEntries(Object.entries(goal.funds).map(([who, cents]) => [who, asTyped(cents)])),
      ).catch(() => {});
    }
  }

  onStep('Shortcuts');
  for (const quick of backup.quickActions) {
    await saveQuickAction(familyId, key, {
      id: quick.id,
      reason: quick.reason,
      amount: asTyped(quick.amount),
      people: quick.people.filter((pid) => here.has(pid)),
    });
  }

  onStep('Allowance');
  await saveAllowance(
    familyId,
    Object.fromEntries(
      Object.entries(backup.allowance)
        .filter(([who]) => who === 'family' || here.has(who))
        .map(([who, p]) => [who, { amount: asTyped(p.amount), every: p.every, start: p.start, paused: p.paused }]),
    ),
  );

  onStep('New codes for the children');
  for (const person of roster) await mintChildCode(familyId, key, person.id);
  return { codes: roster.length };
}
